/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import {
  ExclamationTriangleIcon,
  InfoCircledIcon,
} from '@/shared/ui/radix-icons';
import { Anchor, Text, UnstyledButton } from '@/shared/ui';
import Button from '@/shared/ui/components/basic/Button';
import { cn } from '@/shared/ui/utils/tailwind';
import { GroupLabel } from './GroupLabel';
import { GoogleMark } from './GoogleMark';

type Translate = (key: string, vars?: Record<string, string>) => string;

/**
 * "Continue with Google", in Google's prescribed shell.
 *
 * White ground, their grey border, their four-colour mark: the branding
 * guidelines fix this button's appearance, so it does not take our button
 * styles.
 *
 * @param props - Label, busy state and the click handler.
 * @returns The rendered button.
 */
export const GoogleButton: React.FC<{
  label: string;
  loading?: boolean;
  onClick: () => void;
}> = ({ label, loading = false, onClick }) => (
  <UnstyledButton
    type="button"
    onClick={onClick}
    disabled={loading}
    className={cn(
      'inline-flex h-10 items-center gap-(--space-2-5) rounded-(--radius-md)',
      'border pl-(--space-3) pr-(--space-4)',
      'text-[length:var(--text-body-sm)] font-[weight:var(--font-medium)]',
      'disabled:opacity-60',
    )}
    style={{
      background: 'var(--brand-google-button-bg)',
      color: 'var(--brand-google-button-text)',
      borderColor: 'var(--brand-google-button-border)',
    }}
  >
    <GoogleMark size={18} />
    {label}
  </UnstyledButton>
);

/**
 * One of the two tiles explaining what crosses between Housien and Google.
 *
 * @param props - Direction label, headline and detail.
 * @returns The rendered tile.
 */
export const DirectionTile: React.FC<{
  direction: string;
  headline: string;
  detail: string;
}> = ({ direction, headline, detail }) => (
  <div className="flex flex-col gap-(--space-1) rounded-(--radius-callout) bg-[var(--surface-card)] p-(--space-4)">
    <GroupLabel>{direction}</GroupLabel>
    <Text variant="bodySm" className="font-[weight:var(--font-semibold)] text-[var(--text-primary)] text-pretty">
      {headline}
    </Text>
    <Text variant="small" className="text-[var(--text-secondary)] text-pretty">
      {detail}
    </Text>
  </div>
);

export type BannerTone = 'warning' | 'error';

const BANNER_TONE: Record<BannerTone, { bg: string; fg: string }> = {
  warning: { bg: 'var(--status-warning-bg)', fg: 'var(--status-warning-text)' },
  error: { bg: 'var(--status-error-bg)', fg: 'var(--status-error-text)' },
};

/**
 * A banner naming a problem with the link and the one action that fixes it.
 *
 * @param props - Tone, texts and the action handler.
 * @returns The rendered banner.
 */
export const StateBanner: React.FC<{
  tone: BannerTone;
  title: string;
  body: string;
  cta: string;
  onAction: () => void;
}> = ({ tone, title, body, cta, onAction }) => (
  <div
    role="status"
    className="flex flex-col gap-(--space-2) rounded-(--radius-callout) p-(--space-4)"
    style={{ background: BANNER_TONE[tone].bg }}
  >
    <Text
      component="span"
      className="flex items-center gap-(--space-2) text-[length:var(--text-body-sm)] font-[weight:var(--font-semibold)]"
      style={{ color: BANNER_TONE[tone].fg }}
    >
      {tone === 'error' ? (
        <ExclamationTriangleIcon width={14} height={14} aria-hidden="true" />
      ) : (
        <InfoCircledIcon width={14} height={14} aria-hidden="true" />
      )}
      {title}
    </Text>
    <Text variant="small" className="text-[var(--text-secondary)] text-pretty">
      {body}
    </Text>
    <div>
      <Button type="button" variant="outline" size="sm" onClick={onAction}>
        {cta}
      </Button>
    </div>
  </div>
);

/**
 * The connected account, with the last sync and a way to force one.
 *
 * @param props - Account email, status line, busy state and handler.
 * @returns The rendered card.
 */
export const AccountCard: React.FC<{
  email: string;
  status: string;
  syncLabel: string;
  syncing: boolean;
  onSync: () => void;
}> = ({ email, status, syncLabel, syncing, onSync }) => (
  <div className="flex flex-wrap items-center gap-(--space-3) rounded-(--radius-callout) border border-[var(--border-subtle)] p-(--space-4)">
    <GoogleMark size={24} />
    <div className="flex min-w-0 flex-1 flex-col gap-(--space-0-5)">
      <Text variant="bodySm" className="font-[weight:var(--font-semibold)] text-[var(--text-primary)]">
        {email}
      </Text>
      <Text variant="meta" className="text-[var(--text-secondary)]">
        {status}
      </Text>
    </div>
    <Button
      type="button"
      variant="outline"
      size="sm"
      loading={syncing}
      disabled={syncing}
      onClick={onSync}
    >
      {syncLabel}
    </Button>
  </div>
);

/**
 * The privacy link that sits under anything describing Google data.
 *
 * @param props - The link label.
 * @returns The rendered link.
 */
export const PrivacyLink: React.FC<{ label: string }> = ({ label }) => (
  <Anchor
    href="/privacy/latest"
    target="_blank"
    rel="noopener noreferrer"
    className="text-[length:var(--text-small)] font-[weight:var(--font-semibold)] text-[var(--link-text)] underline"
  >
    {label}
  </Anchor>
);
